import { PaintCommand } from '../core/EditorActions.js';

/**
 * Pencil tool — single-tile and stamp painting with brush size,
 * Shift+click line drawing, and random tile selection.
 *
 * Features:
 *   - Single tile or stamp painting
 *   - Eraser mode (fill with 0)
 *   - Adjustable brush size (1-10, changed via [ ] keys)
 *   - Shift+click draws a line from last painted position
 *   - Random tile mode: when state.randomTiles is set, picks randomly from the set
 *   - Flip/rotate: respects state.tileFlipFlags applied to painted tiles
 */
export class PencilTool {
  constructor(state, mapCanvas, commandHistory, eraseMode = false) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.commandHistory = commandHistory;
    this.eraseMode = eraseMode;
    this._changes = [];
    this._painted = new Set();
    this._lastTile = null; // for Shift+click line drawing
  }

  onDown(tx, ty, e) {
    this._changes = [];
    this._painted.clear();

    if (e?.shiftKey && this._lastTile) {
      // Line drawing: from last position to current
      this._drawLine(this._lastTile.x, this._lastTile.y, tx, ty);
    } else {
      this._paintBrush(tx, ty);
    }

    this._lastTile = { x: tx, y: ty };
  }

  onMove(tx, ty) {
    this._paintBrush(tx, ty);
    this._lastTile = { x: tx, y: ty };
  }

  onUp() {
    if (this._changes.length > 0) {
      const layerIndex = this.state.get('activeLayerIndex');
      const cmd = new PaintCommand(this.state, layerIndex, this._changes);
      this.commandHistory.push(cmd);
    }
    this._changes = [];
    this._painted.clear();
  }

  /**
   * Paint with current brush size centered on tx,ty.
   */
  _paintBrush(tx, ty) {
    const brushSize = this.state.get('brushSize') || 1;
    const half = Math.floor(brushSize / 2);

    for (let dy = -half; dy < brushSize - half; dy++) {
      for (let dx = -half; dx < brushSize - half; dx++) {
        this._paint(tx + dx, ty + dy);
      }
    }
  }

  /**
   * Draw a line of tiles from (x0,y0) to (x1,y1) using Bresenham's algorithm.
   */
  _drawLine(x0, y0, x1, y1) {
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;
    let cx = x0, cy = y0;

    while (true) {
      this._paintBrush(cx, cy);
      if (cx === x1 && cy === y1) break;
      const e2 = 2 * err;
      if (e2 > -dy) { err -= dy; cx += sx; }
      if (e2 < dx) { err += dx; cy += sy; }
    }
  }

  _paint(tx, ty) {
    const state = this.state;
    const mapW = state.get('mapWidth');
    const mapH = state.get('mapHeight');
    const layerIndex = state.get('activeLayerIndex');
    const flipFlags = state.get('tileFlipFlags') || 0;

    if (this.eraseMode) {
      this._paintTile(layerIndex, tx, ty, 0, mapW, mapH);
      return;
    }

    // Random tile mode: pick from randomTiles set
    const randomTiles = state.get('randomTiles');
    if (randomTiles && randomTiles.length > 0) {
      const tileId = randomTiles[Math.floor(Math.random() * randomTiles.length)];
      this._paintTile(layerIndex, tx, ty, tileId | flipFlags, mapW, mapH);
      return;
    }

    const stamp = state.get('stamp');
    if (stamp) {
      for (let sy = 0; sy < stamp.length; sy++) {
        for (let sx = 0; sx < stamp[sy].length; sx++) {
          const tileId = stamp[sy][sx];
          this._paintTile(layerIndex, tx + sx, ty + sy, tileId ? (tileId | flipFlags) : tileId, mapW, mapH);
        }
      }
    } else {
      const tileId = state.get('selectedTileId');
      this._paintTile(layerIndex, tx, ty, tileId | flipFlags, mapW, mapH);
    }
  }

  _paintTile(layerIndex, x, y, tileId, mapW, mapH) {
    if (x < 0 || y < 0 || x >= mapW || y >= mapH) return;
    const key = `${x},${y}`;
    if (this._painted.has(key)) return;
    this._painted.add(key);

    const oldTile = this.state.getTile(layerIndex, x, y);
    if (oldTile === tileId) return;

    this.state.setTile(layerIndex, x, y, tileId);
    this.mapCanvas.invalidateLayer(layerIndex);
    this._changes.push({ x, y, oldTile, newTile: tileId });
  }
}
